import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Trash2, Edit, Search, Wrench, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminServicios() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [servicios, setServicios] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", descripcion: "", categoria: "", precio: "" });
  const [saving, setSaving] = useState(false);

  const fmtCLP = (n) => "$" + (Number(n) || 0).toLocaleString("es-CL");

  const load = () => api.get("/api/servicios").then((r) => setServicios(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.post("/api/servicios/seed").then(load).catch(() => {});
  }, []);

  const uf = (f, v) => setForm((p) => ({ ...p, [f]: v }));
  const openNew = () => { setEditing(null); setForm({ nombre: "", descripcion: "", categoria: "", precio: "" }); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ nombre: s.nombre, descripcion: s.descripcion || "", categoria: s.categoria || "", precio: s.precio || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSaving(true);
    const payload = { ...form, precio: Number(form.precio) || 0 };
    try {
      if (editing) {
        await api.put(`/api/servicios/${editing.id}`, payload);
        toast.success("Servicio actualizado");
      } else {
        await api.post("/api/servicios", payload);
        toast.success("Servicio creado");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`¿Eliminar el servicio "${s.nombre}"?`)) return;
    try { await api.delete(`/api/servicios/${s.id}`); toast.success("Eliminado"); load(); }
    catch { toast.error("Error al eliminar"); }
  };

  const filtered = servicios.filter((s) =>
    s.nombre?.toLowerCase().includes(search.toLowerCase()) ||
    s.categoria?.toLowerCase().includes(search.toLowerCase())
  );
  const categorias = [...new Set(servicios.map((s) => s.categoria).filter(Boolean))].sort();
  const grupos = [...categorias, ...(filtered.some((s) => !s.categoria) ? ["Sin categoría"] : [])];

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Wrench className="h-5 w-5 text-[#0FB4B7]" />
            <span className="text-sm font-semibold text-[#384091]">Catálogo de Servicios</span>
            <Badge className="bg-[#0FB4B7] text-white text-[10px]">{servicios.length}</Badge>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/dashboard")}>
            <ArrowLeft className="h-4 w-4" /><span className="ml-1">Volver</span>
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input placeholder="Buscar por nombre o categoría..." value={search}
              onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
          </div>
          <Button size="sm" className="bg-[#0FB4B7] hover:bg-[#0D999C] text-white" onClick={openNew}>
            <Plus className="h-4 w-4 mr-1" />Nuevo servicio
          </Button>
        </div>

        {servicios.length === 0 && (
          <p className="text-center text-gray-400 py-12">Sin servicios. Crea el primero con "Nuevo servicio".</p>
        )}

        {grupos.map((cat) => {
          const items = filtered.filter((s) => (cat === "Sin categoría" ? !s.categoria : s.categoria === cat));
          if (items.length === 0) return null;
          return (
            <div key={cat}>
              <h3 className="text-sm font-semibold text-[#384091] mb-2 mt-3">{cat}</h3>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b">
                        <th className="text-left px-4 py-2 text-gray-500 font-medium">Servicio</th>
                        <th className="text-right px-4 py-2 text-gray-500 font-medium w-32">Precio</th>
                        <th className="text-right px-4 py-2 text-gray-500 font-medium w-24">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((s) => (
                        <tr key={s.id} className="border-b last:border-0 hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <p className="font-medium text-gray-800">{s.nombre}</p>
                            {s.descripcion && <p className="text-xs text-gray-500">{s.descripcion}</p>}
                          </td>
                          <td className="px-4 py-2 text-right font-medium text-[#384091]">
                            {s.precio ? fmtCLP(s.precio) : "—"}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <button onClick={() => openEdit(s)} className="text-gray-400 hover:text-[#384091] p-1"><Edit className="h-3.5 w-3.5" /></button>
                            <button onClick={() => remove(s)} className="text-gray-400 hover:text-red-500 p-1 ml-1"><Trash2 className="h-3.5 w-3.5" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} servicio</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => uf("nombre", e.target.value)} placeholder="Ej: Mantención preventiva" className="mt-1 bg-white" /></div>
            <div><Label>Descripción</Label><Textarea rows={2} value={form.descripcion} onChange={(e) => uf("descripcion", e.target.value)} placeholder="Descripción del servicio" className="mt-1 bg-white" /></div>
            <div><Label>Categoría</Label><Input value={form.categoria} onChange={(e) => uf("categoria", e.target.value)} placeholder="Ej: Mantención, Instalación..." className="mt-1 bg-white" /></div>
            <div><Label>Precio (CLP)</Label><Input type="number" min="0" value={form.precio} onChange={(e) => uf("precio", e.target.value.replace(/\D/g, ""))} placeholder="Ej: 45000" className="mt-1 bg-white" /></div>
            <Button className="w-full bg-[#384091] hover:bg-[#2D3375] text-white" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {editing ? "Guardar cambios" : "Crear servicio"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
